import { Location } from '@angular/common';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_INSTANCE, MsalService } from '@azure/msal-angular';
import { IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { EMPTY, Observable } from 'rxjs';

import { AppStateService } from '@app/services/app-state.service';
import { AuthenticationService } from '@app/services/authentication.service';
import { NavigationService } from '@app/services/navigation.service';

/**
 * NEO-3626: the single choke point for the interactive-redirect breaker.
 *
 * The three redirect sites the app owns gate themselves, but msal-angular also
 * redirects on its own behalf: MsalGuard and MsalInterceptor each call
 * MsalService directly, so those redirects never reached the breaker. That is
 * the path that kept looping on a build where the breaker was already live —
 * app reaches Ready, NeoAuthGuard delegates to super.canActivate, and the
 * library redirects uncounted (see TESTING.md section 6c).
 *
 * Overriding the two redirect methods here covers every caller — library, app,
 * and anything added later — because they all resolve the same DI token.
 */
@Injectable()
export class NeoMsalService extends MsalService {

  /** Guards against a second refused redirect racing the first one's navigation. */
  private navigatingToAuthCheck = false;

  constructor(
    @Inject(MSAL_INSTANCE) instance: IPublicClientApplication,
    location: Location,
    private readonly appInjector: Injector,
  ) {
    super(instance, location, appInjector);
  }

  public loginRedirect(request?: RedirectRequest): Observable<void> {
    if (!this.allowRedirect('MsalGuard')) {
      return EMPTY;
    }
    return super.loginRedirect(this.withTabState(request));
  }

  public acquireTokenRedirect(request: RedirectRequest): Observable<void> {
    if (!this.allowRedirect('MsalInterceptor')) {
      return EMPTY;
    }
    return super.acquireTokenRedirect(this.withTabState(request));
  }

  /**
   * NEO-4214 (D013/D014/D015): thread this tab's own window.name-derived id
   * through MSAL's OAuth `state` request parameter. MSAL appends it after its
   * own library state and echoes it back unchanged through B2C — a protocol
   * guarantee, unlike window.name itself, which WebKit resets on a
   * cross-origin redirect round-trip (see app.config.ts's restoration helper,
   * which reads this back on the way in).
   *
   * Centralized here rather than at each AuthenticationService call site
   * because loginRedirect/acquireTokenRedirect are the one choke point every
   * interactive redirect already resolves through, app- and library-initiated
   * alike (see the class doc comment above) — covering MsalGuard/
   * MsalInterceptor too, not just the app's own redirect calls.
   *
   * Preserves an already-set `state` rather than overwriting it, though no
   * caller sets one today.
   */
  private withTabState(request: RedirectRequest | undefined): RedirectRequest {
    // `scopes` stays whatever the caller already had (or omitted, for a bare
    // loginRedirect() — MSAL fills defaults from the configured authRequest);
    // this only ever adds `state`, so the cast is safe.
    return { ...request, state: request?.state ?? window.name } as RedirectRequest;
  }

  /**
   * AuthenticationService and AppStateService are resolved lazily rather than
   * injected: both sit upstream of MsalService in the graph, so taking either in
   * the constructor would close a DI cycle.
   *
   * A redirect the app has already gated arrives carrying a one-shot marker, so
   * it stays counted once by its own call site instead of twice here.
   *
   * Which counter a library redirect lands on is decided by the app's state at
   * the moment it fires, NOT by the call site. The interceptor runs during boot
   * too — `performInitialization` loads the user profile and account settings
   * over HTTP before ever reaching Ready — so a not-Ready interceptor redirect is
   * part of an ordinary startup and belongs on the resettable counter. Only a
   * redirect from a Ready app is the loop signature that must survive Ready.
   */
  private allowRedirect(trigger: string): boolean {
    const authenticationService = this.appInjector.get(AuthenticationService);

    if (authenticationService.consumeAppInitiatedRedirect()) {
      return true;
    }

    const allowed = this.isAppReady()
      ? authenticationService.tryBeginLibraryRedirect(trigger)
      : authenticationService.tryBeginInteractiveRedirect(trigger);

    if (allowed) {
      return true;
    }

    authenticationService.emitRedirectLoopFailure();
    this.routeToAuthCheck();
    return false;
  }

  /**
   * Defaults to NOT ready if the state cannot be read. That direction is the safe
   * one: an unreadable state sends the redirect to the resettable counter, so the
   * worst case is weaker loop protection rather than locking a user out of a
   * healthy sign-in.
   */
  private isAppReady(): boolean {
    try {
      return this.appInjector.get(AppStateService).isReady();
    } catch {
      return false;
    }
  }

  private routeToAuthCheck(): void {
    // Synchronous latch. The interceptor and the guard are routinely refused
    // within the same few milliseconds, and `router.url` does not update until a
    // navigation completes — so reading it cannot tell a second caller that the
    // first is already on its way. Only an instance flag set before the call is
    // reliable here.
    if (this.navigatingToAuthCheck) {
      return;
    }

    const router = this.appInjector.get(Router);
    const navigationService = this.appInjector.get(NavigationService);

    if (router.url.startsWith('/auth-check')) {
      return;
    }

    // Very early in a boot the router still reports '/', so fall back to the
    // address bar to keep a real returnUrl.
    const returnUrl = router.url === '/'
      ? window.location.pathname + window.location.search
      : router.url;

    // Build the target before latching: if the tree cannot be produced there is
    // nothing to navigate to, and latching first would suppress every later
    // attempt for the life of the tab.
    let authCheckUrl;
    try {
      authCheckUrl = navigationService.generateAuthCheckUrlTree(returnUrl);
    } catch {
      return;
    }

    this.navigatingToAuthCheck = true;
    try {
      void router.navigateByUrl(authCheckUrl)
        .catch(() => undefined)
        .then(() => {
          this.navigatingToAuthCheck = false;
        });
    } catch {
      // A synchronous throw would otherwise strand the latch, permanently
      // swallowing the recovery navigation.
      this.navigatingToAuthCheck = false;
    }
  }
}
